// Bookie · Charcoal — variant that renders the standalone HTML
// template (Bookie phone-shell, charcoal slate #1A1917 dark shell +
// warm paper #F5EFE6 body text + gold #B89968 accent, Cinzel
// display + Manrope body + JetBrains Mono numerals, geometric
// chevron band ornament) inside a full-bleed iframe. This variant
// has been rebuilt for a CORPORATE PRODUCT LAUNCH — sections are
// HERO / ANNOUNCEMENT / PRODUCT PHOTO GRID / 3-TRACK AGENDA /
// PRESS+GENERAL RSVP / VENUE & MAP / FOOTER. No music, no
// guestbook, no countdown — strictly business.
//
// The HTML lives at /templates/bookie-charcoal.html and is
// byte-identical to the designer source except for the customizer
// script inlined at the top of <body> that swaps the
// names/date/venue/address/time mocks before first paint.
//
// `names` is treated as a SINGLE company name (corporate variant —
// no ampersand splitting); the customizer strips any accidental
// "A & B" form down to its first token.
//
// We pass payload values via the URL because postMessage would
// race the iframe's first paint and the customizer needs to run
// before the IntersectionObserver inside the HTML attaches.

import type { VariantProps } from "./types";
import { getLocale } from "@/lib/i18n";

const TEMPLATE_HTML = "/templates/bookie-charcoal.html";

export function BookieCharcoalStaticVariant({ t, ctx }: VariantProps) {
  const params = new URLSearchParams();
  if (t.names) params.set("names", t.names);
  if (t.date) {
    // Convert dd.mm.yyyy → ISO yyyy-mm-dd for the customizer.
    const m = t.date.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (m) params.set("date", `${m[3]}-${m[2]}-${m[1]}`);
    else params.set("date", t.date);
  }
  if (t.venue) params.set("venue", t.venue);
  if (t.address) params.set("address", t.address);
  if (t.time) params.set("time", t.time);
  // Demo / preview surfaces leave ctx.slug undefined.
  if (ctx.slug) params.set("slug", ctx.slug);
  // This template is intentionally silent — corporate launch, no audio.
  // Cache buster — bump when the static HTML changes.
  params.set("v", "3");

  // Demo/catalog preview (no real invitation slug): default the template's
  // in-iframe language to the app locale, so taklifla.com previews render in
  // English (and a RU viewer sees Russian). The HTMLs read ?lang= (all) and
  // ?default_lang= (newer premium ones) — set both. Owner-published
  // invitations (ctx.slug set) keep their own default_lang / built-in default.
  if (!ctx.slug) {
    const _loc = getLocale();
    if (!params.has("lang")) params.set("lang", _loc);
    if (!params.has("default_lang")) params.set("default_lang", _loc);
  }
  const src = `${TEMPLATE_HTML}?${params.toString()}`;

  return (
    <iframe
      src={src}
      title={t.names || "Atlas Launch"}
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100svh",
        border: "none",
        display: "block",
        // Dark shell — match the slate base so the iframe doesn't
        // flash paper before the HTML paints.
        background: "#1A1917",
      }}
    />
  );
}
